import { Controller } from "stimulus";
import SupercastUploadManager from "packs/episode_upload_manager";

// How long a completed row stays visible before it's cleared out of the panel, purely cosmetic -
// the manager itself keeps the record around indefinitely (see episode_upload_manager.js).
const AUTO_HIDE_COMPLETED_AFTER_MS = 5000;

// Persistent, global upload-status widget. Mounted once in creator_dashboard.html.erb, outside
// the Turbo-swapped <main id="page-main">, so this controller reconnects (and re-seeds itself
// from SupercastUploadManager.getAllUploads()) on every dashboard page - the same idiom
// upload_controller.js uses for its own reattachInFlightUploads(). All upload state lives in the
// manager singleton, never in this controller, so nothing is lost across a reconnect.
export default class UploadStatusWidgetComponentController extends Controller {
  connect() {
    this._rows = new Map(); // localId -> row element
    this._uploadStats = new Map(); // localId -> { startedAt, lastTime, lastBytes, speedBps }

    this.userCollapsed = false;

    this._autoHideTimers = new Map();

    this._onManagerEvent = this.handleManagerEvent.bind(this);
    this._unsubscribeManager = SupercastUploadManager.subscribe(this._onManagerEvent);

    this._onToast = this.handleToast.bind(this);
    window.addEventListener("supercast:upload-toast", this._onToast);

    SupercastUploadManager.getAllUploads().forEach((upload) => this.renderUpload(upload));
    this.updatePanelVisibility();
  }

  disconnect() {
    if (this._unsubscribeManager) {
      this._unsubscribeManager();
      this._unsubscribeManager = null;
    }

    window.removeEventListener("supercast:upload-toast", this._onToast);

    this._autoHideTimers.forEach((timer) => clearTimeout(timer));
    this._autoHideTimers.clear();
  }

  togglePanel() {
    this.userCollapsed = this.panelTarget.classList.toggle("hidden");
  }

  // ---------------------------------------------------------------------------------------
  // Upload rows
  // ---------------------------------------------------------------------------------------

  handleManagerEvent({ type, upload }) {
    if (!upload) return;

    if (type === "cancel") {
      this.removeRow(upload.localId);
      this.updatePanelVisibility();
      return;
    }

    this.renderUpload(upload);
    this.updatePanelVisibility();
  }

  renderUpload(upload) {
    let row = this._rows.get(upload.localId);
    if (!row) {
      row = this.buildRow(upload.localId);
      this._rows.set(upload.localId, row);
      this.listTarget.appendChild(row);
      this.userCollapsed = false;
    }

    this.updateRow(row, upload);
  }

  buildRow(localId) {
    const fragment = this.rowTemplateTarget.content.cloneNode(true);
    const row = fragment.firstElementChild;
    row.dataset.localId = localId;

    // Queried once here rather than in updateRow(), which otherwise re-queries all of these on
    // every manager event - including every single progress tick of an in-flight upload.
    row._els = {
      filename: row.querySelector("[data-filename]"),
      icons: {
        uploading: row.querySelector("[data-icon-uploading]"),
        processing: row.querySelector("[data-icon-processing]"),
        completed: row.querySelector("[data-icon-completed]"),
        error: row.querySelector("[data-icon-error]"),
      },
      statusLabel: row.querySelector("[data-status-label]"),
      speedLabel: row.querySelector("[data-speed-label]"),
      progressTrack: row.querySelector("[data-progress-track]"),
      progressBar: row.querySelector("[data-progress-bar]"),
      pauseResumeButton: row.querySelector("[data-pause-resume-button]"),
      retryButton: row.querySelector("[data-retry-button]"),
      fileInput: row.querySelector("[data-file-input]"),
    };

    row.addEventListener("click", () => this.goToEpisode(row));
    // Pause/resume, retry, and file-picker are nested inside the clickable row - stop the click
    // from also triggering navigation.
    row._els.pauseResumeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.togglePause(localId);
    });
    row._els.retryButton.addEventListener("click", (event) => {
      event.stopPropagation();
      this.retry(localId);
    });
    row._els.fileInput.addEventListener("click", (event) => event.stopPropagation());
    row._els.fileInput.addEventListener("change", (event) => this.onFilePicked(localId, event));

    return row;
  }

  goToEpisode(row) {
    const episodeId = row.dataset.episodeId;
    if (!episodeId) return; // Not resolved yet (upload just started, presigned_upload still in flight)

    window.Turbo.visit(`/dashboard/channels/episodes/${episodeId}`);
  }

  updateRow(row, upload) {
    if (upload.episodeId) row.dataset.episodeId = upload.episodeId;

    const { filename: filenameEl, icons, statusLabel: statusLabelEl, speedLabel: speedLabelEl,
            progressTrack, progressBar, pauseResumeButton, retryButton } = row._els;

    filenameEl.textContent = upload.file?.name || `Episode upload (${upload.mediaType || "file"})`;

    // Icons: uploading and paused both show the spinner (treat paused as still "in progress" visually)
    const iconState = upload.state === "paused" ? "uploading" : upload.state;
    Object.entries(icons).forEach(([state, icon]) => {
      if (icon) icon.classList.toggle("hidden", iconState !== state);
    });

    const isMultipart = upload.provider === "aws" && upload.transportCredentials?.upload_mode === "multipart";

    switch (upload.state) {
      case "uploading": {
        const percentage = upload.bytesTotal ? Math.round((upload.bytesUploaded / upload.bytesTotal) * 100) : 0;
        statusLabelEl.textContent = `Uploading… ${percentage}%`;
        progressTrack.classList.remove("hidden");
        progressBar.style.width = `${percentage}%`;
        retryButton.classList.add("!hidden");

        const statsText = this._statsText(upload);
        speedLabelEl.textContent = statsText;
        speedLabelEl.classList.toggle("hidden", !statsText);

        if (isMultipart) {
          pauseResumeButton.textContent = "Pause";
          pauseResumeButton.classList.remove("hidden");
        } else {
          pauseResumeButton.classList.add("hidden");
        }
        break;
      }
      case "paused": {
        const percentage = upload.bytesTotal ? Math.round((upload.bytesUploaded / upload.bytesTotal) * 100) : 0;
        statusLabelEl.textContent = `Paused · ${percentage}%`;
        progressTrack.classList.remove("hidden");
        progressBar.style.width = `${percentage}%`;
        speedLabelEl.classList.add("hidden");
        retryButton.classList.add("!hidden");
        pauseResumeButton.textContent = "Resume";
        pauseResumeButton.classList.remove("hidden");
        break;
      }
      case "processing":
        statusLabelEl.textContent = "Processing…";
        progressTrack.classList.add("hidden");
        speedLabelEl.classList.add("hidden");
        pauseResumeButton.classList.add("hidden");
        retryButton.classList.add("!hidden");
        break;
      case "completed":
        statusLabelEl.textContent = "Upload complete";
        progressTrack.classList.add("hidden");
        speedLabelEl.classList.add("hidden");
        pauseResumeButton.classList.add("hidden");
        retryButton.classList.add("!hidden");
        this.scheduleAutoHide(upload.localId);
        break;
      case "error":
        statusLabelEl.textContent = upload.errorMessage || "Upload failed";
        progressTrack.classList.add("hidden");
        speedLabelEl.classList.add("hidden");
        pauseResumeButton.classList.add("hidden");
        retryButton.classList.remove("!hidden");
        break;
      default:
        break;
    }
  }

  togglePause(localId) {
    const upload = SupercastUploadManager.getUpload(localId);
    if (!upload) return;

    if (upload.state === "uploading") {
      SupercastUploadManager.pause(localId);
    } else if (upload.state === "paused") {
      SupercastUploadManager.resume(localId);
    }
  }

  // Retries in place if the original File is still cached in memory; otherwise falls back to a
  // file picker scoped to this row and starts a brand-new upload once a file is chosen.
  retry(localId) {
    const resumed = SupercastUploadManager.retry(localId);
    if (resumed) return;

    const row = this._rows.get(localId);
    if (!row) return;

    row._els.fileInput.click();
  }

  onFilePicked(localId, event) {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    const stale = SupercastUploadManager.getUpload(localId);

    SupercastUploadManager.start({
      file,
      episodeId: stale?.episodeId,
      mediaType: stale?.mediaType,
      duration: stale?.duration,
    });

    // Evict the old failed record now that a fresh upload has been started for it, so it doesn't
    // linger in the panel as a stale duplicate row.
    SupercastUploadManager.cancel(localId);
  }

  removeRow(localId) {
    const row = this._rows.get(localId);
    if (!row) return;

    row.remove();
    this._rows.delete(localId);
    this._uploadStats.delete(localId);

    const timer = this._autoHideTimers.get(localId);
    if (timer) {
      clearTimeout(timer);
      this._autoHideTimers.delete(localId);
    }
  }

  // Only called for `completed` uploads (see updateRow) - safe to also forget() the manager's
  // record here, since a completed upload never needs retry()'s cached File again.
  scheduleAutoHide(localId) {
    if (this._autoHideTimers.has(localId)) return;

    const timer = setTimeout(() => {
      this._autoHideTimers.delete(localId);
      this.removeRow(localId);
      SupercastUploadManager.forget(localId);
      this.updatePanelVisibility();
    }, AUTO_HIDE_COMPLETED_AFTER_MS);
    this._autoHideTimers.set(localId, timer);
  }

  updatePanelVisibility() {
    this.panelTarget.classList.toggle("hidden", this._rows.size === 0 || this.userCollapsed);
  }

  // ---------------------------------------------------------------------------------------
  // Toasts - dispatched by SupercastUploadManager on every terminal outcome (upload error,
  // commit error, commit success), regardless of which page is currently open.
  // ---------------------------------------------------------------------------------------

  handleToast(event) {
    const { type, message } = event.detail || {};
    if (!message) return;

    const templateTarget = type === "error" ? this.toastErrorTemplateTarget : this.toastSuccessTemplateTarget;
    const node = templateTarget.content.firstElementChild.cloneNode(true);
    const messageEl = node.querySelector("[data-message]");
    if (messageEl) messageEl.textContent = message;

    this.toastContainerTarget.appendChild(node);
  }

  // Speed + ETA helpers, ported from upload_controller.js#_statsText and friends.
  _statsText(upload) {
    if (!upload.bytesTotal || upload.bytesUploaded >= upload.bytesTotal) return "";

    const now = Date.now();
    const localId = upload.localId;
    if (!this._uploadStats.has(localId)) {
      this._uploadStats.set(localId, { startedAt: now, lastTime: now, lastBytes: 0, speedBps: 0 });
    }
    const stats = this._uploadStats.get(localId);

    const dt = (now - stats.lastTime) / 1000;
    if (dt >= 0.5) {
      const inst = (upload.bytesUploaded - stats.lastBytes) / dt;
      stats.speedBps = stats.speedBps ? (stats.speedBps * 0.7 + inst * 0.3) : inst;
      stats.lastTime = now;
      stats.lastBytes = upload.bytesUploaded;
    }

    const elapsedS = (now - stats.startedAt) / 1000;
    const avgBps = elapsedS > 0 ? upload.bytesUploaded / elapsedS : 0;
    const hybridBps = (stats.speedBps && avgBps) ? (0.5 * stats.speedBps + 0.5 * avgBps) : (stats.speedBps || avgBps);

    if (elapsedS < 3 || upload.bytesUploaded < 5 * 1024 * 1024 || hybridBps <= 0) return "";

    const secondsRemaining = (upload.bytesTotal - upload.bytesUploaded) / hybridBps;
    return `${this._formatBytes(hybridBps)}/s · about ${this._formatDuration(secondsRemaining)} left`;
  }

  _formatBytes(bytes) {
    if (!isFinite(bytes) || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, i);
    return `${value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${units[i]}`;
  }

  _formatDuration(totalSeconds) {
    const s = Math.max(0, Math.round(totalSeconds));
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }
}

UploadStatusWidgetComponentController.targets = [
  "panel",
  "list",
  "rowTemplate",
  "toastContainer",
  "toastSuccessTemplate",
  "toastErrorTemplate",
];
